/**
 * ThinkingModeSelector Component
 *
 * This component allows users to select how the AI should think and respond.
 * Different modes prioritize speed, creativity, or precision.
 *
 * BACKEND INTEGRATION GUIDE:
 *
 * When the backend API is ready to accept thinking modes:
 *
 * 1. Include the selected thinking mode in the message payload:
 *    ```typescript
 *    const payload = {
 *      message: input,
 *      thinkingMode: selectedMode, // Send this to backend
 *      sessionId,
 *      skill,
 *    };
 *    ```
 *
 * 2. Backend should apply the thinking mode to influence:
 *    - Temperature settings
 *    - Token limits
 *    - Response depth
 *    - Reasoning complexity
 *
 * 3. Update the API endpoint to accept and process the thinking mode parameter
 *
 * Current implementation: Stores mode locally, ready for backend integration
 */

import { Scale, Sparkles, Target, Zap } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { THINKING_MODES, type ThinkingMode, type ThinkingModeSelectorProps } from "./types";

// Icon mapping for thinking modes
const THINKING_MODE_ICONS: Record<ThinkingMode, React.ComponentType<{ className?: string }>> = {
  balanced: Scale,
  creative: Sparkles,
  precise: Target,
  fast: Zap,
};

export function ThinkingModeSelector({
  value,
  onChange,
  disabled = false,
}: ThinkingModeSelectorProps) {
  const selectedMode = THINKING_MODES.find((m) => m.value === value);
  const Icon = selectedMode ? THINKING_MODE_ICONS[selectedMode.value] : Scale;

  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted-foreground hidden sm:block" />
      <Select
        value={value}
        onValueChange={onChange}
        disabled={disabled}
      >
        <SelectTrigger
          className="h-7 w-[120px] sm:w-[130px] text-xs"
          aria-label="Select thinking mode"
        >
          <SelectValue placeholder="Mode" />
        </SelectTrigger>
        <SelectContent side="top" align="end">
          {THINKING_MODES.map((mode) => {
            const ModeIcon = THINKING_MODE_ICONS[mode.value];
            return (
              <SelectItem key={mode.value} value={mode.value} className="text-xs py-2">
                <div className="flex items-center gap-2">
                  <ModeIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-xs font-medium">{mode.label}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
